import React, { useState } from 'react'
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
} from '@mui/material'
import {
  Lightbulb,
  Recommend,
  Timeline,
  BarChart,
} from '@mui/icons-material'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  RadialLinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler,
} from 'chart.js'
import { Bar, Radar, Line } from 'react-chartjs-2'
import { TableScrollWrapper } from '@components/layout/TableScrollWrapper'
import {
  useGetPreferenceDashboardQuery,
  useGetPreferenceTimelineQuery,
  useGetPreferenceInsightsQuery,
  useGetRecommendationsQuery,
  DateRange,
  PreferenceBreakdownItem,
} from '../store/api/analyticsApi'

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  RadialLinearScale,
  PointElement,
  LineElement,
  Title,
  ChartTooltip,
  Legend,
  Filler
)

const DATE_RANGE_LABELS: Record<DateRange, string> = {
  '7d': '7 Days',
  '30d': '30 Days',
  '90d': '90 Days',
  '1y': '1 Year',
  all: 'All Time',
}

function SectionHeader({ icon, title }: { icon: React.ReactNode; title: string }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
      {icon}
      <Typography variant="h6" fontWeight="bold">
        {title}
      </Typography>
    </Box>
  )
}

function BreakdownBar({ items, label }: { items: PreferenceBreakdownItem[]; label: string }) {
  if (!items || items.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        Not enough data for {label}.
      </Typography>
    )
  }
  const max = Math.max(...items.map((i) => i.avgRating))
  return (
    <Box>
      {items.map((item) => (
        <Box key={item.name} sx={{ mb: 1.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2">{item.name}</Typography>
            <Typography variant="body2" fontWeight="bold">
              {item.avgRating.toFixed(1)} / 10
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                ({item.count} reviews)
              </Typography>
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={(item.avgRating / 10) * 100}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: 'action.hover',
              '& .MuiLinearProgress-bar': {
                backgroundColor: item.avgRating === max ? 'primary.main' : 'primary.light',
              },
            }}
          />
        </Box>
      ))}
    </Box>
  )
}

const PreferenceAnalytics: React.FC = () => {
  const [dateRange, setDateRange] = useState<DateRange>('all')
  const params = { dateRange }

  const { data: dashboard, isLoading: dashLoading } = useGetPreferenceDashboardQuery(params)
  const { data: timeline, isLoading: timelineLoading } = useGetPreferenceTimelineQuery(params)
  const { data: insights, isLoading: insightsLoading } = useGetPreferenceInsightsQuery(params)
  const { data: recommendations, isLoading: recsLoading } = useGetRecommendationsQuery(params)

  const isLoading = dashLoading || timelineLoading || insightsLoading || recsLoading

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Preference Analytics
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Insights into your cigar taste preferences and personalized recommendations
          </Typography>
        </Box>
        <ToggleButtonGroup
          value={dateRange}
          exclusive
          onChange={(_, v) => v && setDateRange(v)}
          size="small"
        >
          {(['30d', '90d', '1y', 'all'] as DateRange[]).map((r) => (
            <ToggleButton key={r} value={r}>
              {DATE_RANGE_LABELS[r]}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {!isLoading && dashboard && !dashboard.hasEnoughData && (
        <Alert severity="info" sx={{ mb: 3 }}>
          You need at least {dashboard.minimumRequired} reviews to unlock preference analytics.
          You currently have {dashboard.totalReviews}.
        </Alert>
      )}

      {!isLoading && dashboard?.hasEnoughData && (
        <>
          {/* Overview stat row */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {[
              { label: 'Total Reviews', value: dashboard.totalReviews },
              { label: 'Overall Avg Rating', value: `${dashboard.overallAvgRating ?? '--'}/10` },
              {
                label: 'Flavor Avg',
                value: dashboard.flavorAvgRating ? `${dashboard.flavorAvgRating}/10` : 'N/A',
              },
              {
                label: 'Draw Avg',
                value: dashboard.drawAvgRating ? `${dashboard.drawAvgRating}/10` : 'N/A',
              },
            ].map((stat) => (
              <Grid item xs={6} sm={3} key={stat.label}>
                <Card sx={{ textAlign: 'center', py: 1 }}>
                  <CardContent>
                    <Typography variant="h5" fontWeight="bold">
                      {stat.value}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {stat.label}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Breakdown charts */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <SectionHeader icon={<BarChart color="primary" />} title="By Strength" />
                  <BreakdownBar items={dashboard.byStrength ?? []} label="strength" />
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <SectionHeader icon={<BarChart color="primary" />} title="By Wrapper Type" />
                  <BreakdownBar items={dashboard.byWrapper ?? []} label="wrapper" />
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <SectionHeader icon={<BarChart color="primary" />} title="By Origin" />
                  <BreakdownBar items={dashboard.byOrigin ?? []} label="origin" />
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <SectionHeader icon={<BarChart color="primary" />} title="By Vitola" />
                  <BreakdownBar items={dashboard.byVitola ?? []} label="vitola" />
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Top Brands Bar Chart */}
          {dashboard.byBrand && dashboard.byBrand.length > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <SectionHeader icon={<BarChart color="primary" />} title="Top Brands by Rating" />
                <TableScrollWrapper>
                <Bar
                  data={{
                    labels: dashboard.byBrand.map((b) => b.name),
                    datasets: [
                      {
                        label: 'Avg Rating',
                        data: dashboard.byBrand.map((b) => b.avgRating),
                        backgroundColor: 'rgba(99, 102, 241, 0.7)',
                        borderColor: 'rgba(99, 102, 241, 1)',
                        borderWidth: 1,
                      },
                    ],
                  }}
                  options={{
                    responsive: true,
                    scales: { y: { min: 0, max: 10, ticks: { stepSize: 2 } } },
                    plugins: { legend: { display: false } },
                  }}
                />
                </TableScrollWrapper>
              </CardContent>
            </Card>
          )}

          {/* Radar chart of category averages */}
          {dashboard.overallAvgRating &&
            dashboard.flavorAvgRating &&
            dashboard.drawAvgRating &&
            dashboard.burnAvgRating && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <SectionHeader icon={<BarChart color="primary" />} title="Flavor Profile Radar" />
                  <Box sx={{ maxWidth: 400, mx: 'auto' }}>
                    <Radar
                      data={{
                        labels: ['Overall', 'Flavor', 'Draw', 'Burn'],
                        datasets: [
                          {
                            label: 'Your Averages',
                            data: [
                              dashboard.overallAvgRating,
                              dashboard.flavorAvgRating ?? 0,
                              dashboard.drawAvgRating ?? 0,
                              dashboard.burnAvgRating ?? 0,
                            ],
                            backgroundColor: 'rgba(99, 102, 241, 0.2)',
                            borderColor: 'rgba(99, 102, 241, 1)',
                            borderWidth: 2,
                            pointBackgroundColor: 'rgba(99, 102, 241, 1)',
                          },
                        ],
                      }}
                      options={{
                        scales: { r: { min: 0, max: 10, ticks: { stepSize: 2 } } },
                      }}
                    />
                  </Box>
                </CardContent>
              </Card>
            )}
        </>
      )}

      {/* Timeline */}
      {!isLoading && timeline && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <SectionHeader icon={<Timeline color="primary" />} title="Preference Evolution" />
            {!timeline.hasEnoughData ? (
              <Alert severity="info">
                Need at least {timeline.minimumRequired} total reviews to show timeline. You have{' '}
                {timeline.totalReviews}.
              </Alert>
            ) : timeline.timeline.length > 0 ? (
              <TableScrollWrapper>
              <Line
                data={{
                  labels: timeline.timeline.map((t) => t.period),
                  datasets: [
                    {
                      label: 'Avg Rating',
                      data: timeline.timeline.map((t) => t.avgRating),
                      borderColor: 'rgba(99, 102, 241, 1)',
                      backgroundColor: 'rgba(99, 102, 241, 0.1)',
                      tension: 0.3,
                      fill: true,
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  scales: { y: { min: 0, max: 10 } },
                  plugins: { legend: { display: false } },
                }}
              />
              </TableScrollWrapper>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No timeline data available for this date range.
              </Typography>
            )}
          </CardContent>
        </Card>
      )}

      {/* Insights */}
      {!isLoading && insights?.hasEnoughData && insights.insights.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <SectionHeader icon={<Lightbulb color="warning" />} title="Your Insights" />
            <List dense disablePadding>
              {insights.insights.map((insight, i) => (
                <React.Fragment key={i}>
                  <ListItem disableGutters>
                    <ListItemText primary={insight} />
                  </ListItem>
                  {i < insights.insights.length - 1 && <Divider component="li" />}
                </React.Fragment>
              ))}
            </List>
          </CardContent>
        </Card>
      )}

      {/* Recommendations */}
      {!isLoading && recommendations && (
        <Card>
          <CardContent>
            <SectionHeader icon={<Recommend color="success" />} title="Recommended for You" />
            {!recommendations.hasEnoughData ? (
              <Alert severity="info">
                Need at least {recommendations.minimumRequired} reviews for recommendations. You have{' '}
                {recommendations.totalReviews}.
              </Alert>
            ) : recommendations.recommendations.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No new recommendations at this time. Try reviewing more cigars!
              </Typography>
            ) : (
              <>
                {recommendations.preferenceProfile && (
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                      Based on:
                    </Typography>
                    {recommendations.preferenceProfile.topStrength && (
                      <Chip size="small" label={`Strength: ${recommendations.preferenceProfile.topStrength}`} />
                    )}
                    {recommendations.preferenceProfile.topWrapper && (
                      <Chip size="small" label={`Wrapper: ${recommendations.preferenceProfile.topWrapper}`} />
                    )}
                    {recommendations.preferenceProfile.topOrigin && (
                      <Chip size="small" label={`Origin: ${recommendations.preferenceProfile.topOrigin}`} />
                    )}
                  </Box>
                )}
                <Grid container spacing={2}>
                  {recommendations.recommendations.map((rec) => (
                    <Grid item xs={12} sm={6} md={4} key={rec.cigarLineId}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="subtitle2" fontWeight="bold" noWrap>
                            {rec.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            {rec.brandName}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                            {rec.strength && <Chip label={rec.strength} size="small" variant="outlined" />}
                            {rec.wrapper && <Chip label={rec.wrapper} size="small" variant="outlined" />}
                            {rec.origin && <Chip label={rec.origin} size="small" variant="outlined" />}
                          </Box>
                          {rec.reasons.length > 0 && (
                            <Typography variant="caption" color="text.secondary">
                              {rec.reasons[0]}
                            </Typography>
                          )}
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </>
            )}
          </CardContent>
        </Card>
      )}
    </Box>
  )
}

export default PreferenceAnalytics
